import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
  Button,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
} from "reactstrap";

import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { apiGet, apiPost, apiPut, apiDelete } from "helpers/apiService";
import SafeField from "helpers/SafeField";
import ActionEdit from "components/buttons/actionEdit";
import ActionDelete from "components/buttons/actionDelete";

const FieldValue = () => {
  const [list, setList] = useState([]);
  const [definitions, setDefinitions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState({});

  useEffect(() => {
    fetchDefinitions();
    fetchList();
  }, []);

  const toggle = () => setIsOpen(!isOpen);

  /* ================= FETCH ================= */

  const fetchDefinitions = () => {
    apiGet("FieldDef/all")
      .then(res => setDefinitions(res.data))
      .catch(() => toast.error("Failed to load field definitions"));
  };

  const fetchList = () => {
    apiGet("FieldValue/all")
      .then(res => setList(res.data))
      .catch(() => toast.error("Failed to load field values"));
  };

  /* ================= CREATE ================= */

  const handleCreate = (values, { setSubmitting, resetForm }) => {
    toast.loading("Saving...");

    const payload = {
      fieldDefUId: parseInt(values.fieldDefUId, 10),
      fieldValueName: values.fieldValueName,
    };

    apiPost("FieldValue", payload)
      .then(() => {
        toast.dismiss();
        toast.success("Saved successfully");
        fetchList();
        resetForm();
      })
      .catch(() => {
        toast.dismiss();
        toast.error("Save failed");
      })
      .finally(() => setSubmitting(false));
  };

  /* ================= UPDATE ================= */

  const handleUpdate = (values, { setSubmitting }) => {
    toast.loading("Updating...");

    const payload = {
        id: values.id,
      fieldDefId: values.fieldDefId,     // GUID from backend
      fieldDefUId: values.fieldDefUId,   // int id
      fieldValueName: values.fieldValueName,
    };

    apiPut("FieldValue", payload)
      .then(() => {
        toast.dismiss();
        toast.success("Updated successfully");
        fetchList();
        toggle();
      })
      .catch(() => {
        toast.dismiss();
        toast.error("Update failed");
      })
      .finally(() => setSubmitting(false));
  };

  /* ================= DELETE ================= */

  const handleDelete = id => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will be deleted permanently!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
    }).then(result => {
      if (!result.isConfirmed) return;

      apiDelete(`FieldValue/${id}`)
        .then(() => {
          toast.success("Deleted successfully");
          fetchList();
        })
        .catch(() => toast.error("Delete failed"));
    });
  };

  const openEdit = item => {
    setSelected(item);
    toggle();
  };

  /* ================= VALIDATION ================= */

  const validationSchema = Yup.object({
    fieldDefUId: Yup.number().required("Field definition required"),
    fieldValueName: Yup.string().required("Value is required"),
  });

  return (
    <Container fluid className="page-content">
      <ToastContainer autoClose={2000} />
 <Row className="mb20">
                    <Col xl="12">
                      <Breadcrumbs
                        title="General Settings"
                        breadcrumbItem="Field Value"
                      />
                    </Col>
                  </Row>
        
      <Row>
        <Col xl="12">
          {/* ===== ADD FORM ===== */}
          <Card>
            <CardBody>
             

              <Formik
                initialValues={{
                  fieldDefUId: "",
                  fieldValueName: "",
                }}
                validationSchema={validationSchema}
                onSubmit={handleCreate}
              >
                {({ errors, touched, handleChange }) => (
                  <Form>
                    <Row>
                      <Col md="4">
                        <FormGroup>
                          <Label>Field Definition *</Label>
                          <SafeField
                            name="fieldDefUId"
                            as="select"
                            onChange={handleChange}
                            className={`form-control ${
                              errors.fieldDefUId && touched.fieldDefUId ? "is-invalid" : ""
                            }`}
                          >
                            <option value="">Select</option>
                            {definitions.map(d => (
                              <option key={d.id} value={d.id}>
                                {d.fieldDefName}
                              </option>
                            ))}
                          </SafeField>
                          <ErrorMessage
                            name="fieldDefUId"
                            component="div"
                            className="invalid-feedback"
                          />
                        </FormGroup>
                      </Col>

                      <Col md="4">
                        <FormGroup>
                          <Label>Field Value *</Label>
                          <SafeField
                            name="fieldValueName"
                            onChange={handleChange}
                            className={`form-control ${
                              errors.fieldValueName && touched.fieldValueName ? "is-invalid" : ""
                            }`}
                          />
                          <ErrorMessage
                            name="fieldValueName"
                            component="div"
                            className="invalid-feedback"
                          />
                        </FormGroup>
                      </Col>

                      <Col md="2" className="">
                        <Button type="submit" color="primary" className="mt-4">
                          Save
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                )}
              </Formik>
            </CardBody>
          </Card>

          {/* ===== LIST ===== */}
          <Card>
            <CardBody>
              <h5>Field Value List</h5>

              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Definition</th>
                      <th>Value</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((item, i) => (
                      <tr key={item.fieldDefId || i}>
                        <td>{i + 1}</td>
                        <td>
                          {item.fieldDefName ||
                            item.fieldDefinition?.fieldDefName ||
                            "-"}
                        </td>
                        <td>{item.fieldValueName}</td>
                        <td>
                            <ActionEdit onClick={() => openEdit(item)} />{" "}
                            <ActionDelete onClick={() => handleDelete(item.fieldDefId)} />
                         
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* ===== EDIT OFFCANVAS ===== */}
      <Offcanvas isOpen={isOpen} toggle={toggle} direction="end">
        <OffcanvasHeader toggle={toggle}>Update Field Value</OffcanvasHeader>
        <OffcanvasBody>
          <Formik
            enableReinitialize
            initialValues={selected}
            validationSchema={validationSchema}
            onSubmit={handleUpdate}
          >
            {({ handleChange }) => (
              <Form>
                <FormGroup>
                  <Label>Field Definition *</Label>
                  <SafeField
                    name="fieldDefUId"
                    as="select"
                    onChange={handleChange}
                    className="form-control"
                  >
                    <option value="">Select</option>
                    {definitions.map(d => (
                      <option key={d.id} value={d.id}>
                        {d.fieldDefName}
                      </option>
                    ))}
                  </SafeField>
                </FormGroup>

                <FormGroup>
                  <Label>Field Value *</Label>
                  <SafeField
                    name="fieldValueName"
                    onChange={handleChange}
                    className="form-control"
                  />
                </FormGroup>

                <Button type="submit" color="primary">
                  Update
                </Button>
              </Form>
            )}
          </Formik>
        </OffcanvasBody>
      </Offcanvas>
    </Container>
  );
};

export default FieldValue;
